import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Input } from '@ludus/ui'
import { directusLogin, LoginError } from '@/lib/api/auth.ts'
import { useAuthStore } from '@/shared/store/useAuthStore.ts'

/**
 * Login gate — operators sign in with their Directus account. On success the
 * Directus token is stored and the app renders; the engine validates that token
 * back against Directus. Shown by `App.tsx` whenever there's no token.
 */
export function LoginScreen() {
  const { t } = useTranslation()
  const setToken = useAuthStore((s) => s.setToken)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return
    setError(null)
    setBusy(true)
    try {
      const token = await directusLogin(email.trim(), password)
      setToken(token)
    } catch (err) {
      const kind = err instanceof LoginError ? err.kind : 'unknown'
      setError(
        kind === 'invalid_credentials'
          ? t('auth.login.errorCredentials', { defaultValue: 'Wrong email or password.' })
          : kind === 'network'
            ? t('auth.login.errorNetwork', { defaultValue: 'Cannot reach the server. Try again.' })
            : t('auth.login.errorGeneric', { defaultValue: 'Could not sign in. Try again.' }),
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background-app p-6">
      <div className="flex w-full max-w-sm flex-col gap-6 rounded-xl border border-border bg-background-secondary p-6 shadow-lg">
        <div className="flex flex-col gap-1">
          <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted">
            {t('auth.login.brand', { defaultValue: 'Ludus Console' })}
          </span>
          <h1 className="text-lg font-semibold text-foreground">
            {t('auth.login.title', { defaultValue: 'Sign in' })}
          </h1>
          <p className="text-xs text-muted">
            {t('auth.login.subtitle', { defaultValue: 'Use your Directus account.' })}
          </p>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">
              {t('auth.login.email', { defaultValue: 'Email' })}
            </span>
            <Input
              type="email"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">
              {t('auth.login.password', { defaultValue: 'Password' })}
            </span>
            <Input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>

          {error && (
            <p role="alert" className="text-xs font-medium text-toast-error-text">
              {error}
            </p>
          )}

          <Button type="submit" disabled={busy || !email || !password}>
            {busy
              ? t('auth.login.signingIn', { defaultValue: 'Signing in…' })
              : t('auth.login.submit', { defaultValue: 'Sign in' })}
          </Button>
        </form>
      </div>
    </div>
  )
}
